const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();
// Compression utilities using gzip
async function compress(data) {
    const stream = new Blob([data]).stream();
    const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
    const compressedBlob = await new Response(compressedStream).blob();
    return new Uint8Array(await compressedBlob.arrayBuffer());
}
async function decompress(data) {
    const stream = new Blob([data]).stream();
    const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'));
    const decompressedBlob = await new Response(decompressedStream).blob();
    return new Uint8Array(await decompressedBlob.arrayBuffer());
}
function toBase64(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++)
        binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}
function fromBase64(b64) {
    const binaryString = atob(b64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++)
        bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
}
async function deriveKey(password, salt) {
    const baseKey = await crypto.subtle.importKey('raw', TEXT_ENCODER.encode(password), 'PBKDF2', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'PBKDF2',
        salt,
        iterations: 310000,
        hash: 'SHA-256'
    }, baseKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
export async function encryptWithPassword(password, data) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const key = await deriveKey(password, salt.buffer);
    // Encode JSON to bytes
    const encoded = TEXT_ENCODER.encode(JSON.stringify(data));
    // Compress before encryption (much better compression ratio)
    const compressed = await compress(encoded);
    // Encrypt the compressed data
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, compressed);
    return {
        iv: toBase64(iv.buffer),
        salt: toBase64(salt.buffer),
        ciphertext: toBase64(ciphertext),
        version: 2 // Version 2 indicates compressed data
    };
}
export async function decryptWithPassword(password, payload) {
    const iv = fromBase64(payload.iv);
    const salt = fromBase64(payload.salt);
    const key = await deriveKey(password, salt);
    // Decrypt the ciphertext
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, key, fromBase64(payload.ciphertext));
    let plaintext;
    // Handle both compressed (v2) and uncompressed (v1) payloads
    if (payload.version >= 2) {
        // Version 2: decompress after decryption
        const decompressed = await decompress(new Uint8Array(decrypted));
        plaintext = decompressed.buffer;
    }
    else {
        // Version 1 (legacy): no compression
        plaintext = decrypted;
    }
    const decoded = TEXT_DECODER.decode(plaintext);
    return JSON.parse(decoded);
}
export async function deriveStableVaultId(password) {
    const input = TEXT_ENCODER.encode(password);
    const hash = await crypto.subtle.digest('SHA-256', input);
    return 'vault_' + toBase64(hash).replace(/[^a-zA-Z0-9]/g, '').slice(0, 22);
}
export async function deriveCloudVaultId(password) {
    // Use a different salt/prefix for cloud vaults to ensure separation
    const input = TEXT_ENCODER.encode('cloud:' + password);
    const hash = await crypto.subtle.digest('SHA-256', input);
    return 'cvault_' + toBase64(hash).replace(/[^a-zA-Z0-9]/g, '').slice(0, 22);
}
